<script setup lang="ts">
import type { TemperatureUnit } from "@/stores/settings.store";
import { computed } from "vue";
import { colorForTemperature } from "rainowls-shared";
import {
  conditionProgress,
  measurementRange,
  type ValueRange,
} from "@/utils/conditionBar";
import { useUnitConversion } from "@/composables/useUnitConversion";
import { Skeleton } from "@/components/ui/skeleton";

const props = defineProps<{
  unitPreference: TemperatureUnit;
  label: string;
  kind: "temperature" | "humidity" | "wind" | "pressure";
  current: number | null;
  observed?: ValueRange | null;
  forecast?: ValueRange | null;
  scale: ValueRange;
  unit: string;
  loading?: boolean;
}>();

const { convertTemperature, convertSpeed, labels } = useUnitConversion(
  () => props.unitPreference,
);

const bar = computed(() =>
  conditionProgress(
    props.current,
    props.observed ?? null,
    props.forecast ?? null,
    props.scale,
  ),
);

const gradient = computed(() => {
  if (props.kind !== "temperature") return `var(--bar-${props.kind}-gradient)`;
  // Same canonical Fahrenheit palette as native, ending at the filled value.
  const { low, high: ceiling, fill } = bar.value;
  const high = low + ((ceiling - low) * fill) / 100;
  return `linear-gradient(90deg, ${Array.from({ length: 25 }, (_, i) => colorForTemperature(low + ((high - low) * i) / 24, "Metric")).join(", ")})`;
});

const forecastRange = computed(
  () => props.forecast && measurementRange(...props.forecast),
);

const description = computed(() => {
  /** Format the available minimum and maximum values. */
  const range = (value: ValueRange) =>
    `${format(value[0])} to ${format(value[1])}`;
  return (
    `${props.label}. Current: ${props.current === null ? "unavailable" : format(props.current)}. ` +
    (props.observed
      ? `Observed daily range: ${range(props.observed)}. `
      : "No observed daily range. ") +
    (props.forecast
      ? `Forecast: ${range(props.forecast)}.`
      : "No forecast comparison.")
  );
});

/** Format an available metric value for display. */
function format(value: number) {
  return props.kind === "temperature"
    ? `${convertTemperature(value)}${labels.value.temperature}`
    : props.kind === "wind"
      ? `${convertSpeed(value)} ${labels.value.speed}`
      : `${Math.round(value)}${props.kind === "humidity" ? "" : " "}${props.unit}`;
}
</script>

<template>
  <div
    class="condition-value-display w-full min-w-0"
    :class="kind === 'temperature' ? 'min-h-4' : 'min-h-[23px]'"
  >
    <Skeleton
      v-if="loading"
      class="h-1.5 w-full rounded-full"
      aria-hidden="true"
    />
    <template v-else-if="current !== null">
      <div
        class="h-1.5 w-full min-w-0 overflow-hidden rounded-full bg-[var(--bar-track)]"
        role="img"
        :aria-label="description"
        :title="description"
      >
        <span
          class="block h-full rounded-[inherit]"
          :style="{ background: gradient, width: bar.fill + '%' }"
        />
      </div>
      <div
        v-if="kind !== 'temperature' && forecastRange"
        class="mt-1 flex justify-between gap-3 text-[10px] leading-[1.3] text-text-muted tabular-nums"
        :aria-label="`${label} forecast range for today`"
      >
        <span>Min {{ format(forecastRange[0]) }}</span>
        <span>Max {{ format(forecastRange[1]) }}</span>
      </div>
    </template>
    <p
      v-else
      class="text-[11px] leading-[1.4] text-text-muted"
      :title="description"
    >
      No current reading
    </p>
  </div>
</template>
